import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";

interface CvData {
  id: string;
  title: string;
  summary: string;
  experience: { title: string; company: string; startDate: string; endDate: string; current: boolean; description: string }[];
  education: { school: string; degree: string; field: string; graduationYear: string }[];
  skills: string[];
  certifications: { name: string; issuer: string; year: string }[];
  languages: { name: string; level: string }[];
  address: string;
  birth_date: string;
  gender: string;
  website: string;
}

interface ApplicantInfo {
  name: string;
  email: string;
  phone: string;
  address: string;
  birth_date: string;
  gender: string;
}

interface DisplayApp {
  id: string;
  userId: string;
  applicantName: string;
  applicantEmail: string;
  applicantPhone: string;
  applicantAddress: string;
  applicantBirthDate: string;
  applicantGender: string;
  jobTitle: string;
  jobCompany: string;
  jobLocation: string;
  hasCv: boolean;
  cvId: string | null;
  coverLetter: string | null;
  status: string;
  appliedAt: string;
}

const statusConfig: Record<string, { label: string; className: string }> = {
  pending: { label: "Chờ duyệt", className: "bg-amber-100 text-amber-700" },
  reviewed: { label: "Phỏng vấn", className: "bg-blue-100 text-blue-700" },
  accepted: { label: "Trúng tuyển", className: "bg-green-100 text-green-700" },
  rejected: { label: "Từ chối", className: "bg-red-100 text-red-600" },
};

const typeLabels: Record<string, string> = {
  pending: "Chờ duyệt",
  reviewed: "Phỏng vấn",
  accepted: "Trúng tuyển",
  rejected: "Từ chối",
};

export default function ApplicantsList() {
  const { user } = useAuth();
  const [applications, setApplications] = useState<DisplayApp[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "pending" | "reviewed" | "accepted" | "rejected">("all");
  const [search, setSearch] = useState("");
  const [selectedCv, setSelectedCv] = useState<CvData | null>(null);
  const [selectedApplicant, setSelectedApplicant] = useState<ApplicantInfo | null>(null);
  const [loadingCv, setLoadingCv] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  const fetchApplications = useCallback(async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setError(null);

      const { data: myJobs, error: jobsError } = await supabase
        .from("vietnamcv_jobs")
        .select("id")
        .eq("owner_id", user.id);

      if (jobsError) {
        setError("Không thể tải danh sách ứng viên.");
        setIsLoading(false);
        return;
      }

      if (!myJobs || myJobs.length === 0) {
        setApplications([]);
        setIsLoading(false);
        return;
      }

      const jobIds = myJobs.map((j) => j.id);

      const { data: apps, error: appError } = await supabase
        .from("vietnamcv_applications")
        .select("*")
        .in("job_id", jobIds)
        .order("applied_at", { ascending: false });

      if (appError) {
        setError("Không thể tải danh sách ứng viên.");
        setIsLoading(false);
        return;
      }

      if (!apps || apps.length === 0) {
        setApplications([]);
        setIsLoading(false);
        return;
      }

      const profileIds = [...new Set(apps.map((a: { user_id: string }) => a.user_id))];
      const appJobIds = [...new Set(apps.map((a: { job_id: string }) => a.job_id))];

      const [{ data: profiles }, { data: jobsData }] = await Promise.all([
        supabase.from("vietnamcv_profiles").select("id, name, email, phone, address, birth_date, gender").in("id", profileIds),
        supabase.from("vietnamcv_jobs").select("id, title, company, location").in("id", appJobIds),
      ]);

      const profileMap = new Map((profiles || []).map((p: { id: string; name: string; email: string; phone: string; address: string; birth_date: string; gender: string }) => [p.id, p]));
      const jobMap = new Map((jobsData || []).map((j: { id: string; title: string; company: string; location: string }) => [j.id, j]));

      const mapped: DisplayApp[] = apps.map((row: { id: string; user_id: string; job_id: string; cv_id: string | null; cover_letter: string | null; status: string; applied_at: string }) => {
        const profile = profileMap.get(row.user_id);
        const job = jobMap.get(row.job_id);
        return {
          id: row.id,
          userId: row.user_id,
          applicantName: profile?.name || "Ẩn danh",
          applicantEmail: profile?.email || "",
          applicantPhone: profile?.phone || "",
          applicantAddress: profile?.address || "",
          applicantBirthDate: profile?.birth_date || "",
          applicantGender: profile?.gender || "",
          jobTitle: job?.title || "",
          jobCompany: job?.company || "",
          jobLocation: job?.location || "",
          hasCv: !!row.cv_id,
          cvId: row.cv_id,
          coverLetter: row.cover_letter,
          status: row.status || "pending",
          appliedAt: row.applied_at,
        };
      });

      setApplications(mapped);
    } catch {
      setError("Không thể tải danh sách ứng viên.");
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const filtered = applications.filter((app) => {
    if (filter !== "all" && app.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        app.applicantName.toLowerCase().includes(q) ||
        app.jobTitle.toLowerCase().includes(q) ||
        app.applicantEmail.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const statusCounts = {
    pending: applications.filter((a) => a.status === "pending").length,
    reviewed: applications.filter((a) => a.status === "reviewed").length,
    accepted: applications.filter((a) => a.status === "accepted").length,
    rejected: applications.filter((a) => a.status === "rejected").length,
  };

  const viewCv = async (app: DisplayApp) => {
    // Auto mark as reviewed (if pending)
    if (app.status === "pending") {
      setViewingId(app.id);
      const { error: updateError } = await supabase
        .from("vietnamcv_applications")
        .update({ status: "reviewed" })
        .eq("id", app.id);

      if (!updateError) {
        setApplications((prev) =>
          prev.map((a) => (a.id === app.id ? { ...a, status: "reviewed" } : a))
        );
      }
      setViewingId(null);
    }

    if (!app.cvId) return;

    setLoadingCv(true);
    try {
      const { data, error: cvError } = await supabase
        .from("vietnamcv_cvs")
        .select("*")
        .eq("id", app.cvId)
        .maybeSingle();

      if (cvError || !data) {
        setSelectedCv(null);
        return;
      }

      setSelectedCv({
        id: data.id,
        title: data.title || "",
        summary: data.summary || "",
        experience: data.experience || [],
        education: data.education || [],
        skills: data.skills || [],
        certifications: data.certifications || [],
        languages: data.languages || [],
        address: data.address || "",
        birth_date: data.birth_date || "",
        gender: data.gender || "",
        website: data.website || "",
      });
    } catch {
      setSelectedCv(null);
    } finally {
      setLoadingCv(false);
    }
  };

  const viewApplicantInfo = async (app: DisplayApp) => {
    // Auto mark as reviewed (if pending)
    if (app.status === "pending") {
      setViewingId(app.id);
      const { error: updateError } = await supabase
        .from("vietnamcv_applications")
        .update({ status: "reviewed" })
        .eq("id", app.id);

      if (!updateError) {
        setApplications((prev) =>
          prev.map((a) => (a.id === app.id ? { ...a, status: "reviewed" } : a))
        );
      }
      setViewingId(null);
    }

    setSelectedApplicant({
      name: app.applicantName,
      email: app.applicantEmail,
      phone: app.applicantPhone,
      address: app.applicantAddress,
      birth_date: app.applicantBirthDate,
      gender: app.applicantGender,
    });
  };

  const updateStatus = async (appId: string, newStatus: string) => {
    setUpdatingId(appId);
    try {
      const { error: updateError } = await supabase
        .from("vietnamcv_applications")
        .update({ status: newStatus })
        .eq("id", appId);

      if (updateError) return;

      setApplications((prev) =>
        prev.map((a) => (a.id === appId ? { ...a, status: newStatus } : a))
      );
    } catch {
      // silently fail
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatShortDate = (dateStr: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100">
          <h3 className="font-heading font-semibold text-foreground-950 text-base">Danh sách ứng viên</h3>
        </div>
        <div className="p-10 space-y-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 animate-pulse">
              <div className="w-10 h-10 rounded-full bg-background-200/80" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-1/3 bg-background-200/80 rounded" />
                <div className="h-3 w-1/2 bg-background-200/50 rounded" />
              </div>
              <div className="h-7 w-24 bg-background-200/60 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100">
          <h3 className="font-heading font-semibold text-foreground-950 text-base">Danh sách ứng viên</h3>
        </div>
        <div className="flex flex-col items-center py-12 gap-3">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-xl text-red-500"></i>
          </div>
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            onClick={() => { setIsLoading(true); fetchApplications(); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-sm"></i>
            <span>Thử lại</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
        <div>
          <h3 className="font-heading font-semibold text-foreground-950 text-base">Danh sách ứng viên</h3>
          <p className="text-xs text-foreground-500 mt-0.5">Xem và quản lý hồ sơ ứng tuyển vào tin của bạn</p>
        </div>

        <div className="relative w-full sm:w-64">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm ứng viên hoặc vị trí..."
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex items-center bg-background-100 rounded-lg p-1">
          {(["all", "pending", "reviewed", "accepted", "rejected"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`relative px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                filter === s
                  ? "bg-background-50 text-foreground-900"
                  : "text-foreground-500 hover:text-foreground-700"
              }`}
            >
              {s === "all" ? "Tất cả" : typeLabels[s]}
              {s !== "all" && statusCounts[s] > 0 && (
                <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold ${
                  s === "pending" ? "bg-amber-200 text-amber-800" :
                  s === "reviewed" ? "bg-blue-200 text-blue-800" :
                  s === "accepted" ? "bg-green-200 text-green-800" :
                  "bg-red-200 text-red-800"
                }`}>
                  {statusCounts[s]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-background-50 rounded-xl border border-background-200/70 flex flex-col items-center py-16 gap-3">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-user-search-line text-xl text-foreground-400"></i>
          </div>
          <p className="text-sm text-foreground-500">
            {applications.length === 0
              ? "Chưa có ứng viên nào ứng tuyển"
              : "Không tìm thấy ứng viên phù hợp"}
          </p>
          <p className="text-xs text-foreground-400">
            {applications.length === 0
              ? "Ứng viên sẽ xuất hiện ở đây khi họ ứng tuyển vào tin của bạn"
              : "Thử thay đổi bộ lọc hoặc từ khóa tìm kiếm"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((app) => {
            const cfg = statusConfig[app.status] || statusConfig.pending;
            return (
              <div
                key={app.id}
                className="bg-background-50 rounded-xl border border-background-200/70 p-4 md:p-5 hover:border-background-300/80 transition-colors group"
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold">
                        {app.applicantName.charAt(0).toUpperCase()}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1.5">
                        <p className="text-sm font-semibold text-foreground-900">{app.applicantName}</p>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap w-fit ${cfg.className}`}>
                          {cfg.label}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-500 mt-0.5">
                        Ứng tuyển: <strong className="text-foreground-700">{app.jobTitle}</strong> — {app.jobLocation}
                      </p>
                      <p className="text-[11px] text-foreground-400 mt-0.5">
                        {formatDate(app.appliedAt)}
                      </p>
                    </div>
                  </div>

                  {app.coverLetter && (
                    <div className="p-2.5 bg-background-100 rounded-lg">
                      <p className="text-xs font-medium text-foreground-600 mb-0.5">Thư ứng tuyển:</p>
                      <p className="text-xs text-foreground-700 line-clamp-2">{app.coverLetter}</p>
                    </div>
                  )}

                  {/* 4 text buttons at the bottom */}
                  <div className="flex items-center gap-2 md:gap-3 flex-wrap pt-1 border-t border-background-100">
                    {app.hasCv && (
                      <button
                        onClick={() => viewCv(app)}
                        className="text-xs font-medium text-primary-600 hover:text-primary-800 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-file-text-line mr-1"></i>Xem CV
                      </button>
                    )}
                    <button
                      onClick={() => viewApplicantInfo(app)}
                      className="text-xs font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-user-line mr-1"></i>Xem TT ứng viên
                    </button>

                    <span className="w-px h-4 bg-background-300 hidden sm:block"></span>

                    {app.status !== "reviewed" && (
                      <button
                        onClick={() => updateStatus(app.id, "reviewed")}
                        disabled={updatingId === app.id}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        <i className="ri-phone-line mr-1"></i>Phỏng vấn
                      </button>
                    )}
                    {app.status !== "accepted" && (
                      <button
                        onClick={() => updateStatus(app.id, "accepted")}
                        disabled={updatingId === app.id}
                        className="text-xs font-medium text-green-600 hover:text-green-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        <i className="ri-check-line mr-1"></i>Trúng tuyển
                      </button>
                    )}
                    {app.status !== "rejected" && (
                      <button
                        onClick={() => updateStatus(app.id, "rejected")}
                        disabled={updatingId === app.id}
                        className="text-xs font-medium text-red-500 hover:text-red-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        <i className="ri-close-line mr-1"></i>Từ chối
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between mt-4 px-2">
        <p className="text-xs text-foreground-500">
          Hiển thị <strong className="text-foreground-700">{filtered.length}</strong> / {applications.length} ứng viên
        </p>
      </div>

      {/* Loading overlay */}
      {loadingCv && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-background-50 rounded-2xl p-10 shadow-2xl flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
            <p className="text-sm text-foreground-600">Đang tải...</p>
          </div>
        </div>
      )}

      {/* CV Modal */}
      {selectedCv && !loadingCv && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm overflow-y-auto py-10"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedCv(null);
          }}
        >
          <div className="bg-background-50 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 my-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
              <div>
                <h3 className="font-heading font-bold text-lg text-foreground-950">
                  {selectedCv.title || "CV Ứng viên"}
                </h3>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {selectedCv.address && `${selectedCv.address} • `}
                  {selectedCv.birth_date && `${selectedCv.birth_date}`}
                </p>
              </div>
              <button
                onClick={() => setSelectedCv(null)}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {selectedCv.summary && (
                <div>
                  <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Giới thiệu</h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">{selectedCv.summary}</p>
                </div>
              )}

              {selectedCv.experience && selectedCv.experience.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Kinh nghiệm làm việc</h4>
                  <div className="space-y-4">
                    {selectedCv.experience.map((exp, i) => (
                      <div key={i} className="pl-4 border-l-2 border-accent-200">
                        <p className="text-sm font-semibold text-foreground-900">{exp.title}</p>
                        <p className="text-xs text-foreground-600">{exp.company} • {exp.startDate} — {exp.current ? "Hiện tại" : exp.endDate}</p>
                        {exp.description && <p className="text-xs text-foreground-600 mt-1">{exp.description}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedCv.education && selectedCv.education.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Học vấn</h4>
                  <div className="space-y-3">
                    {selectedCv.education.map((edu, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0">
                          <i className="ri-graduation-cap-line text-sm"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">{edu.degree} - {edu.field}</p>
                          <p className="text-xs text-foreground-500">{edu.school} • Tốt nghiệp {edu.graduationYear}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedCv.skills && selectedCv.skills.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Kỹ năng</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCv.skills.map((skill, i) => (
                      <span key={i} className="inline-flex px-2.5 py-1 bg-background-100 text-foreground-700 rounded-md text-xs">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedCv.certifications && selectedCv.certifications.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Chứng chỉ</h4>
                  <div className="space-y-2">
                    {selectedCv.certifications.map((cert, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-award-line text-accent-500 text-sm"></i>
                        </div>
                        <span className="text-sm text-foreground-700">{cert.name}</span>
                        <span className="text-xs text-foreground-400">— {cert.issuer}, {cert.year}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedCv.languages && selectedCv.languages.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Ngôn ngữ</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCv.languages.map((lang, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-100 text-accent-700 rounded-md text-xs font-medium">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-global-line text-[10px]"></i>
                        </div>
                        {lang.name} — {lang.level}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-background-200">
              <button
                onClick={() => setSelectedCv(null)}
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Applicant Info Modal */}
      {selectedApplicant && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm overflow-y-auto py-10"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedApplicant(null);
          }}
        >
          <div className="bg-background-50 rounded-2xl shadow-2xl w-full max-w-lg mx-4 my-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
              <h3 className="font-heading font-bold text-lg text-foreground-950">Thông tin ứng viên</h3>
              <button
                onClick={() => setSelectedApplicant(null)}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b border-background-100">
                <div className="w-14 h-14 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center shrink-0">
                  <span className="text-xl font-bold">{selectedApplicant.name.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-foreground-950">{selectedApplicant.name}</h4>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">Email</label>
                  <p className="text-sm text-foreground-800">{selectedApplicant.email || "—"}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">Số điện thoại</label>
                  <p className="text-sm text-foreground-800">{selectedApplicant.phone || "—"}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">Ngày sinh</label>
                  <p className="text-sm text-foreground-800">{selectedApplicant.birth_date || "—"}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">Giới tính</label>
                  <p className="text-sm text-foreground-800">
                    {selectedApplicant.gender === "male" ? "Nam" : selectedApplicant.gender === "female" ? "Nữ" : selectedApplicant.gender || "—"}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">Địa chỉ</label>
                  <p className="text-sm text-foreground-800">{selectedApplicant.address || "—"}</p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-background-200">
                <button
                  onClick={() => setSelectedApplicant(null)}
                  className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}